import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, apiErr, mediaUrl } from "../../lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, CalendarCheck, CalendarDays, FileText, MessageSquare, BedDouble, Star,
  Settings, LogOut, Plus, Trash2, Pencil, X, TrendingUp, Users, Home as HomeIcon, RefreshCw, Plug, Gift, Phone, ShoppingBag, Euro, ClipboardList, KeyRound, Receipt, Download, Menu, Landmark, ShieldCheck, Image as ImageIcon, UtensilsCrossed, LogIn,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import CalendarTab, { BookingDetailsDialog } from "./CalendarTab";
import PhotoManager from "./PhotoManager";
import Beds24Tab from "./Beds24Tab";
import GiftCardsTab from "./GiftCardsTab";
import MealOrdersTab from "./MealOrdersTab";
import ContratTab from "./ContratTab";
import PhoneBookingDialog from "./PhoneBookingDialog";
import BoutiqueTab from "./BoutiqueTab";
import FinanceTab from "./FinanceTab";
import InvoicesTab from "./InvoicesTab";
import TouristTaxTab from "./TouristTaxTab";
import BookingsRecap from "./BookingsRecap";
import RegistreTab from "./RegistreTab";
import ArrivalsTab from "./ArrivalsTab";
import PoliceTab from "./PoliceTab";
import SecurityTab from "./SecurityTab";
import { ArrivalStepsEditor } from "./ArrivalStepsEditor";

const STATUS = {
  bookings: ["demande", "confirmée", "manual", "expirée", "annulée"],
  quotes: ["nouvelle", "à traiter", "devis envoyé", "confirmée", "refusée"],
  messages: ["nouveau", "traité"],
};

const BOOKING_STATUS = {
  en_attente_paiement: { label: "En attente de paiement", cls: "bg-amber-50 text-amber-700 ring-amber-200" },
  demande: { label: "En attente de paiement", cls: "bg-amber-50 text-amber-700 ring-amber-200" },
  "confirmée": { label: "Confirmée", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  manual: { label: "Réservation directe", cls: "bg-brand-50 text-brand ring-brand/20" },
  blocked: { label: "Externe (canal)", cls: "bg-slate-100 text-slate-600 ring-slate-200" },
  no_show: { label: "Non-présentation", cls: "bg-red-50 text-red-600 ring-red-200" },
  "expirée": { label: "Expirée", cls: "bg-slate-100 text-slate-500 ring-slate-200" },
  "annulée": { label: "Annulée", cls: "bg-slate-100 text-slate-500 ring-slate-200" },
};

function StatusBadge({ status, channel, isBlock }) {
  if (status === "blocked") {
    const low = (channel || "").toLowerCase();
    let s;
    if (isBlock || low === "blocage") s = { label: "Blocage manuel", cls: "bg-slate-100 text-slate-600 ring-slate-200" };
    else if (low === "airbnb") s = { label: "Airbnb", cls: "bg-rose-50 text-rose-600 ring-rose-200" };
    else if (low.includes("booking")) s = { label: "Booking.com", cls: "bg-blue-50 text-blue-700 ring-blue-200" };
    else if (low === "direct") s = { label: "Réservation directe", cls: "bg-brand-50 text-brand ring-brand/20" };
    else if (channel) s = { label: channel, cls: "bg-slate-100 text-slate-600 ring-slate-200" };
    else s = { label: "Externe (canal)", cls: "bg-slate-100 text-slate-600 ring-slate-200" };
    return <span data-testid="booking-status-badge" className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ring-1 ring-inset whitespace-nowrap ${s.cls}`}>{s.label}</span>;
  }
  const s = BOOKING_STATUS[status] || { label: status || "—", cls: "bg-slate-100 text-slate-600 ring-slate-200" };
  return <span data-testid="booking-status-badge" className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ring-1 ring-inset whitespace-nowrap ${s.cls}`}>{s.label}</span>;
}

const NAV_GROUPS = [
  ["Pilotage", [
    ["dashboard", "Tableau de bord", LayoutDashboard],
    ["finance", "Finances", Euro],
  ]],
  ["Réservations", [
    ["calendar", "Calendrier", CalendarDays],
    ["bookings", "Réservations", CalendarCheck],
    ["arrivals", "Arrivées & départs", LogIn],
    ["meal-orders", "Commandes repas", UtensilsCrossed],
    ["invoices", "Journal des factures", Receipt],
    ["registre", "Registre voyageurs", ClipboardList],
    ["police", "Fiches de police", ShieldCheck],
    ["tourist-tax", "Taxe de séjour", Landmark],
    ["inbox", "Demandes & avis", MessageSquare],
  ]],
  ["Contenu du site", [
    ["accommodations", "Hébergements", BedDouble],
    ["boutique", "Boutique", ShoppingBag],
    ["giftcards", "Bons cadeaux", Gift],
    ["contrat", "Contrat de location", FileText],
  ]],
  ["Configuration", [
    ["beds24", "Synchro Beds24", Plug],
    ["settings", "Paramètres", Settings],
    ["security", "Sécurité", KeyRound],
  ]],
];
const TABS = NAV_GROUPS.flatMap(([, items]) => items);

const INBOX_SUBS = [
  ["quotes", "Demandes de devis"],
  ["messages", "Messages"],
  ["reviews", "Avis"],
];

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600 mb-1 block">{label}</span>
      <input {...props} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand" />
    </label>
  );
}

function QuoteDetail({ row }) {
  const dt = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("fr-FR", { dateStyle: "long" }); } catch { return s; } };
  const items = [
    ["Prénom / Nom", `${row.prenom || ""} ${row.nom || ""}`.trim() || "—"],
    ["Entreprise", row.entreprise || "—"],
    ["Email", row.email || "—"],
    ["Téléphone", row.telephone || "—"],
    ["Type d'événement", row.type_evenement || "—"],
    ["Date souhaitée", (row.date_souhaitee ? dt(row.date_souhaitee) : "—") + (row.date_flexible ? " · flexible" : "")],
    ["Participants", row.participants != null ? `${row.participants} pers.` : "—"],
    ["Hébergements souhaités", row.hebergement != null ? `${row.hebergement} chambre(s)` : "—"],
    ["Nombre de nuits", row.nuits != null && row.nuits !== "" ? row.nuits : "—"],
    ["Budget indicatif", row.budget || "—"],
    ["Repas souhaités", (row.repas && row.repas.length) ? row.repas.join(", ") : "—"],
    ["Équipements", (row.equipements && row.equipements.length) ? row.equipements.join(", ") : "—"],
    ["Demande reçue le", dt(row.created_at)],
  ];
  return (
    <div className="mt-3 rounded-xl border border-brand/15 bg-brand-50/40 p-4" data-testid={`quote-detail-${row.id}`}>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {items.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 text-xs border-b border-brand/10 pb-1.5">
            <span className="text-ink-400 shrink-0">{k}</span>
            <span className="text-ink-800 font-medium text-right break-words">{v}</span>
          </div>
        ))}
      </div>
      {row.commentaire && (
        <div className="mt-3">
          <p className="text-xs text-ink-400 mb-1">Message / précisions du client</p>
          <p className="text-sm text-ink-800 whitespace-pre-wrap">{row.commentaire}</p>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [inboxSub, setInboxSub] = useState("quotes");
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const [bookingSort, setBookingSort] = useState("recent");
  const [bookingSource, setBookingSource] = useState("all");
  const [stats, setStats] = useState(null);
  const [data, setData] = useState([]);
  const [settings, setSettings] = useState(null);
  const [edit, setEdit] = useState(null);
  const [openQuotes, setOpenQuotes] = useState({});

  useEffect(() => { if (!checking && !user) nav("/admin/login"); }, [checking, user, nav]);

  const loadStats = useCallback(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const loadTab = useCallback((tb) => {
    if (tb === "dashboard") return loadStats();
    if (["calendar", "beds24", "giftcards", "contrat", "boutique", "security", "invoices", "tourist-tax", "meal-orders", "arrivals"].includes(tb)) return;
    if (tb === "settings") return api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    if (tb === "inbox") return;
    api.get(`/admin/${tb}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [loadStats]);

  useEffect(() => {
    if (!user) return;
    if (tab === "inbox") api.get(`/admin/${inboxSub}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
    else loadTab(tab);
  }, [tab, inboxSub, user, loadTab]);

  if (checking || !user) return <div className="min-h-screen grid place-items-center text-ink-400">Chargement…</div>;

  const sortedBookings = (() => {
    if (tab !== "bookings") return data;
    if (!Array.isArray(data)) return [];
    const matchSource = (row) => {
      const ch = (row.smoobu_channel || "").toLowerCase();
      switch (bookingSource) {
        case "airbnb": return ch === "airbnb";
        case "booking": return ch.includes("booking");
        case "direct": return row.source === "en_ligne" || ch === "direct";
        case "telephone": return row.source === "telephone" || row.status === "manual";
        case "blocage": return !!row.smoobu_is_block || ch === "blocage";
        default: return true;
      }
    };
    const t = (d) => (d ? new Date(d).getTime() : 0);
    const cmp = {
      recent: (a, b) => t(b.created_at || b.confirmed_at) - t(a.created_at || a.confirmed_at),
      arrivee: (a, b) => (a.arrivee || "").localeCompare(b.arrivee || ""),
      nom: (a, b) => (a.nom || "").localeCompare(b.nom || ""),
      montant: (a, b) => (b.total || 0) - (a.total || 0),
    }[bookingSort] || (() => 0);
    return data.filter(matchSource).sort(cmp);
  })();

  const bookingBadges = (row) => {
    const today = new Date().toISOString().slice(0, 10);
    const out = [];
    if (row.arrivee === today) out.push(["Arrive aujourd'hui", "bg-emerald-100 text-emerald-700"]);
    if (row.depart === today) out.push(["Part aujourd'hui", "bg-sky-100 text-sky-700"]);
    if (row.status === "en_attente_paiement" || (row.payment_url && row.payment_status !== "paid" && row.status !== "expirée" && row.status !== "annulée")) {
      out.push(["Paiement en attente", "bg-amber-100 text-amber-700"]);
    } else if (row.status === "confirmée" && (row.balance || 0) > 0 && (row.depart || "") >= today) {
      out.push([`À encaisser · ${row.balance} €`, "bg-amber-100 text-amber-800"]);
    }
    return out;
  };  const activeColl = tab === "inbox" ? inboxSub : tab;

  const reloadActive = () => { if (tab === "inbox") api.get(`/admin/${inboxSub}`).then((r) => setData(r.data)).catch(() => {}); else loadTab(tab); };
  const markDepositPaid = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/mark-deposit-paid`);
      toast.success("Acompte encaissé sur place · réservation confirmée");
      reloadActive();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur, réessayez."); }
  };

  const setStatus = async (coll, id, status) => {
    await api.put(`/admin/${coll}/${id}`, { status });
    toast.success("Statut mis à jour");
    loadTab(coll);
  };

  const remove = async (coll, id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    await api.delete(`/admin/${coll}/${id}`);
    toast.success("Supprimé");
    loadTab(coll);
  };

  const saveItem = async (coll, item) => {
    try {
      const payload = { ...item };
      if (typeof payload.amenities === "string") payload.amenities = payload.amenities.split(",").map((s) => s.trim()).filter(Boolean);
      if (payload.id) await api.put(`/admin/${coll}/${payload.id}`, payload);
      else await api.post(`/admin/${coll}`, payload);
      toast.success("Enregistré");
      setEdit(null);
      loadTab(coll);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveSettings = async () => {
    try { await api.put("/admin/settings", settings); toast.success("Paramètres enregistrés"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd);
    return data.url;
  };
  const setSpace = async (key, file) => {
    if (!file) return;
    try { const url = await uploadImage(file); setSettings((s) => ({ ...s, spaces: { ...(s.spaces || {}), [key]: url } })); toast.success("Image ajoutée — cliquez sur Enregistrer pour valider."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addGallery = async (files) => {
    if (!files || !files.length) return;
    try {
      const urls = [];
      for (const f of Array.from(files)) urls.push(await uploadImage(f));
      setSettings((s) => ({ ...s, gallery: [...urls, ...(s.gallery || [])] }));
      toast.success(`${urls.length} photo(s) ajoutée(s) en tête — cliquez sur Enregistrer.`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const removeGallery = (i) => setSettings((s) => ({ ...s, gallery: (s.gallery || []).filter((_, idx) => idx !== i) }));
  const moveGallery = (i, dir) => setSettings((s) => {
    const g = [...(s.gallery || [])];
    const j = i + dir;
    if (j < 0 || j >= g.length) return s;
    [g[i], g[j]] = [g[j], g[i]];
    return { ...s, gallery: g };
  });
  const setMenu = (updater) => setSettings((s) => ({ ...s, bocaux_menu: updater(s.bocaux_menu || { courses: [], drinks: [] }) }));
  const setCourse = (ci, f, v) => setMenu((m) => { const c = [...m.courses]; c[ci] = { ...c[ci], [f]: v }; return { ...m, courses: c }; });
  const setItem = (ci, ii, f, v) => setMenu((m) => { const c = [...m.courses]; const it = [...c[ci].items]; it[ii] = { ...it[ii], [f]: v }; c[ci] = { ...c[ci], items: it }; return { ...m, courses: c }; });
  const addItem = (ci) => setMenu((m) => { const c = [...m.courses]; c[ci] = { ...c[ci], items: [...(c[ci].items || []), { name: "", desc: "" }] }; return { ...m, courses: c }; });
  const removeItem = (ci, ii) => setMenu((m) => { const c = [...m.courses]; c[ci] = { ...c[ci], items: c[ci].items.filter((_, i) => i !== ii) }; return { ...m, courses: c }; });
  const setDrink = (di, f, v) => setMenu((m) => { const d = [...(m.drinks || [])]; d[di] = { ...d[di], [f]: v }; return { ...m, drinks: d }; });
  const addDrink = () => setMenu((m) => ({ ...m, drinks: [...(m.drinks || []), { name: "", price: 0 }] }));
  const removeDrink = (di) => setMenu((m) => ({ ...m, drinks: (m.drinks || []).filter((_, i) => i !== di) }));
  const setFormula = (fi, f, v) => setMenu((m) => { const arr = [...(m.formulas || [])]; arr[fi] = { ...arr[fi], [f]: v }; return { ...m, formulas: arr }; });
  const toggleFormulaCourse = (fi, ck) => setMenu((m) => { const arr = [...(m.formulas || [])]; const cur = arr[fi].courses || []; arr[fi] = { ...arr[fi], courses: cur.includes(ck) ? cur.filter((x) => x !== ck) : [...cur, ck] }; return { ...m, formulas: arr }; });
  const addFormula = () => setMenu((m) => ({ ...m, formulas: [...(m.formulas || []), { key: `f${Date.now()}`, label: "Nouvelle formule", desc: "", price: 0, courses: [], popular: false }] }));
  const removeFormula = (fi) => setMenu((m) => ({ ...m, formulas: (m.formulas || []).filter((_, i) => i !== fi) }));
  const setGMenu = (updater) => setSettings((s) => ({ ...s, galettes_menu: updater(s.galettes_menu || { sections: [] }) }));
  const setGSection = (si, f, v) => setGMenu((m) => { const sec = [...(m.sections || [])]; sec[si] = { ...sec[si], [f]: v }; return { ...m, sections: sec }; });
  const setGItem = (si, ii, f, v) => setGMenu((m) => { const sec = [...(m.sections || [])]; const it = [...(sec[si].items || [])]; it[ii] = { ...it[ii], [f]: v }; sec[si] = { ...sec[si], items: it }; return { ...m, sections: sec }; });
  const addGItem = (si) => setGMenu((m) => { const sec = [...(m.sections || [])]; sec[si] = { ...sec[si], items: [...(sec[si].items || []), { name: "", desc: "", price: 0 }] }; return { ...m, sections: sec }; });
  const removeGItem = (si, ii) => setGMenu((m) => { const sec = [...(m.sections || [])]; sec[si] = { ...sec[si], items: (sec[si].items || []).filter((_, i) => i !== ii) }; return { ...m, sections: sec }; });
  const addGSection = () => setGMenu((m) => ({ ...m, sections: [...(m.sections || []), { key: `s${Date.now()}`, label: "Nouvelle section", items: [] }] }));
  const removeGSection = (si) => setGMenu((m) => ({ ...m, sections: (m.sections || []).filter((_, i) => i !== si) }));
  const L = settings?.livret || {};
  const setLivret = (k, v) => setSettings((s) => ({ ...s, livret: { ...(s.livret || {}), [k]: v } }));
  const setLivretReminder = (i, v) => setSettings((s) => { const arr = [...((s.livret || {}).reminders || [])]; arr[i] = v; return { ...s, livret: { ...(s.livret || {}), reminders: arr } }; });
  const addLivretReminder = () => setSettings((s) => ({ ...s, livret: { ...(s.livret || {}), reminders: [...((s.livret || {}).reminders || []), ""] } }));
  const removeLivretReminder = (i) => setSettings((s) => ({ ...s, livret: { ...(s.livret || {}), reminders: ((s.livret || {}).reminders || []).filter((_, x) => x !== i) } }));
  const setLivretSupplement = (i, v) => setSettings((s) => { const arr = [...((s.livret || {}).supplements || [])]; arr[i] = v; return { ...s, livret: { ...(s.livret || {}), supplements: arr } }; });
  const addLivretSupplement = () => setSettings((s) => ({ ...s, livret: { ...(s.livret || {}), supplements: [...((s.livret || {}).supplements || []), ""] } }));
  const removeLivretSupplement = (i) => setSettings((s) => ({ ...s, livret: { ...(s.livret || {}), supplements: ((s.livret || {}).supplements || []).filter((_, x) => x !== i) } }));
  const setLivretPlace = (i, k, v) => setSettings((s) => { const arr = [...((s.livret || {}).places || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...s, livret: { ...(s.livret || {}), places: arr } }; });
  const addLivretPlace = () => setSettings((s) => ({ ...s, livret: { ...(s.livret || {}), places: [...((s.livret || {}).places || []), { name: "", distance: "", desc: "" }] } }));
  const removeLivretPlace = (i) => setSettings((s) => ({ ...s, livret: { ...(s.livret || {}), places: ((s.livret || {}).places || []).filter((_, x) => x !== i) } }));

  const sidebarNav = (
    <nav className="flex-1 p-3 space-y-6 overflow-y-auto">
      {NAV_GROUPS.map(([group, items]) => (
        <div key={group}>
          <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{group}</p>
          <div className="space-y-0.5">
            {items.map(([id, label, Icon]) => {
              const active = tab === id;
              return (
                <button key={id} onClick={() => { setTab(id); setNavOpen(false); }} data-testid={`admin-tab-${id}`}
                  aria-current={active ? "page" : undefined}
                  className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? "text-brand bg-brand-50 before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1 before:bg-brand before:rounded-r-full" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"}`}>
                  <Icon size={18} strokeWidth={1.5} className="shrink-0" /> <span className="text-left">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );

  const logoutBtn = (
    <button onClick={async () => { await logout(); nav("/admin/login"); }} className="m-3 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors" data-testid="admin-logout">
      <LogOut size={18} strokeWidth={1.5} /> Déconnexion
    </button>
  );

  return (
    <div className="flex h-screen w-full bg-[#FAFAFA] text-slate-900 overflow-hidden font-sans">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-slate-200 bg-white h-full z-10">
        <div className="h-16 flex items-center px-6 border-b border-slate-200 shrink-0">
          <div>
            <p className="font-heading text-lg text-slate-900 leading-none">La Bégaudière</p>
            <p className="text-[11px] text-slate-400 mt-1">Administration</p>
          </div>
        </div>
        {sidebarNav}
        {logoutBtn}
      </aside>

      {/* Mobile drawer */}
      {navOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setNavOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-white flex flex-col shadow-2xl">
            <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200 shrink-0">
              <p className="font-heading text-lg text-slate-900">La Bégaudière</p>
              <button onClick={() => setNavOpen(false)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="admin-drawer-close"><X size={18} /></button>
            </div>
            {sidebarNav}
            {logoutBtn}
          </div>
        </div>
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        {/* Topbar */}
        <header className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-xl flex items-center gap-3 px-4 sm:px-6 sticky top-0 z-40 shrink-0">
          <button onClick={() => setNavOpen(true)} className="md:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100" data-testid="admin-menu-toggle"><Menu size={20} /></button>
          <h1 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 truncate">{TABS.find((t) => t[0] === tab)?.[1]}</h1>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden sm:block text-xs text-slate-400 truncate max-w-[180px]">{user?.email}</span>
            <div className="h-8 w-8 rounded-full bg-brand-50 text-brand grid place-items-center text-sm font-semibold shrink-0">{(user?.email || "A")[0].toUpperCase()}</div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="max-w-6xl mx-auto p-5 sm:p-8 space-y-6">

          {/* DASHBOARD */}
          {tab === "dashboard" && stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                ["Arrivées du jour", stats.arrivals_today, CalendarCheck],
                ["Départs du jour", stats.departures_today, CalendarCheck],
                ["Réservations à venir", stats.upcoming, TrendingUp],
                ["Réservations totales", stats.bookings_total, CalendarCheck],
                ["Devis (nouveaux)", stats.quotes_new, FileText],
                ["Messages (nouveaux)", stats.messages_new, MessageSquare],
                ["Hébergements", stats.accommodations, HomeIcon],
                ["Avis", stats.reviews, Star],
              ].map(([label, val, Icon], i) => (
                <div key={i} data-testid={`dash-kpi-${i}`} className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col gap-3 hover:-translate-y-1 hover:shadow-lg hover:border-slate-300 transition-[transform,box-shadow,border-color] duration-300">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-500">{label}</span>
                    <Icon size={18} strokeWidth={1.5} className="text-brand" />
                  </div>
                  <div className="text-3xl font-bold tracking-tight text-slate-900">{val}</div>
                </div>
              ))}
            </div>
          )}

          {/* CALENDAR */}
          {tab === "calendar" && <CalendarTab onNewBooking={() => setPhoneOpen(true)} />}
          {tab === "meal-orders" && <MealOrdersTab />}
          {tab === "arrivals" && <ArrivalsTab />}
          {tab === "finance" && <FinanceTab />}
          {tab === "invoices" && <InvoicesTab />}
          {tab === "tourist-tax" && <TouristTaxTab />}
          {tab === "registre" && <RegistreTab />}
          {tab === "police" && <PoliceTab />}
          {tab === "beds24" && <Beds24Tab />}
          {tab === "giftcards" && <GiftCardsTab />}
          {tab === "boutique" && <BoutiqueTab />}
          {tab === "contrat" && <ContratTab />}
          {tab === "security" && <SecurityTab />}

          {/* Bouton nouvelle réservation téléphone (Réservations) */}
          {tab === "bookings" && (
            <>
              <BookingsRecap rows={data} />
              <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <button onClick={() => setPhoneOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-5 py-2.5 text-sm font-medium hover:bg-brand-600" data-testid="new-phone-booking-btn">
                  <Phone size={16} /> Nouvelle réservation (téléphone)
                </button>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-xs font-medium text-slate-500">
                    Source
                    <select value={bookingSource} onChange={(e) => setBookingSource(e.target.value)} data-testid="bookings-source-filter" className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-brand">
                      <option value="all">Toutes</option>
                      <option value="airbnb">Airbnb</option>
                      <option value="booking">Booking.com</option>
                      <option value="direct">Directe (site)</option>
                      <option value="telephone">Téléphone</option>
                      <option value="blocage">Blocage manuel</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-2 text-xs font-medium text-slate-500">
                    Trier par
                    <select value={bookingSort} onChange={(e) => setBookingSort(e.target.value)} data-testid="bookings-sort" className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-brand">
                      <option value="recent">Plus récentes</option>
                      <option value="arrivee">Date d'arrivée</option>
                      <option value="nom">Nom (A-Z)</option>
                      <option value="montant">Montant (élevé)</option>
                    </select>
                  </label>
                </div>
              </div>
            </>
          )}

          {/* Inbox sub-navigation */}
          {tab === "inbox" && (
            <div className="mb-4 flex gap-2 flex-wrap" data-testid="inbox-subnav">
              {INBOX_SUBS.map(([id, lbl]) => (
                <button key={id} onClick={() => setInboxSub(id)} data-testid={`inbox-sub-${id}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${inboxSub === id ? "bg-brand text-white" : "bg-white border border-black/5 text-ink-600 hover:border-brand"}`}>
                  {lbl}
                </button>
              ))}
            </div>
          )}

          {/* BOOKINGS / QUOTES / MESSAGES tables */}
          {["bookings", "quotes", "messages"].includes(activeColl) && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {data.length === 0 ? <p className="p-8 text-center text-slate-400">Aucune donnée.</p> : (
                <div className="divide-y divide-slate-100">
                  {(tab === "bookings" ? sortedBookings : data).map((row) => (
                    <div key={row.id} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-slate-50 transition-colors" data-testid={`admin-row-${row.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-ink-900 text-sm flex flex-wrap items-center gap-1.5">
                          <span>{row.nom || row.author || "—"} {row.prenom || ""}
                          {row.accommodation_name ? ` · ${row.accommodation_name}` : ""}
                          {row.entreprise ? ` · ${row.entreprise}` : ""}</span>
                          {row.package_name && <span className="inline-flex items-center rounded-full bg-brand-50 text-brand px-2 py-0.5 text-[11px] font-medium" data-testid={`row-package-${row.id}`}>🎁 {row.package_name}</span>}
                          {row.source === "en_ligne" && <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[11px] font-medium">En ligne</span>}
                          {row.source === "telephone" && <span className="inline-flex items-center rounded-full bg-brand-50 text-brand px-2 py-0.5 text-[11px] font-medium">Téléphone</span>}
                        </p>
                        {tab === "bookings" && bookingBadges(row).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`row-badges-${row.id}`}>
                            {bookingBadges(row).map(([lbl, cls], i) => (
                              <span key={i} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${cls}`}>{lbl}</span>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-ink-400 mt-0.5 truncate">
                          {row.email || ""} {row.telephone ? `· ${row.telephone}` : ""}
                          {row.arrivee ? ` · ${row.arrivee} → ${row.depart}` : ""}
                          {(row.adultes != null || row.enfants != null) ? ` · ${row.adultes || 0} adulte(s)${row.enfants ? `, ${row.enfants} enfant(s)` : ""}` : (row.voyageurs ? ` · ${row.voyageurs} voyageur(s)` : "")}
                          {row.type_evenement ? ` · ${row.type_evenement}` : ""}
                          {row.participants ? ` · ${row.participants} pers.` : ""}
                        </p>
                        {activeColl === "quotes" && (
                          <>
                            <button onClick={() => setOpenQuotes((o) => ({ ...o, [row.id]: !o[row.id] }))} data-testid={`toggle-quote-${row.id}`}
                              className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline">
                              {openQuotes[row.id] ? "Masquer le détail ▲" : "Voir le devis complet ▼"}
                            </button>
                            {openQuotes[row.id] && <QuoteDetail row={row} />}
                          </>
                        )}
                        {(row.total != null || row.deposit != null) && (
                          <p className="text-xs text-ink-500 mt-1">
                            {row.total != null && <>Total {row.total} €</>}
                            {row.deposit != null && <> · Acompte {row.deposit} € {row.payment_status === "paid" ? (row.deposit_paid_onsite ? "(payé sur place ✓)" : "(payé)") : "(en attente)"}</>}
                            {row.balance != null && (row.balance_paid
                              ? <> · Solde {row.balance} € payé sur place ✓</>
                              : <> · Solde {row.balance} € à régler sur place (espèces ou CB)</>)}
                            {row.late_checkout ? " · départ tardif" : ""}
                            {row.bocaux_total > 0 ? ` · Bocaux ${row.bocaux_total} € (sur place)` : ""}
                          </p>
                        )}
                        {(row.message || (activeColl !== "quotes" && row.commentaire)) && <p className="text-xs text-ink-500 mt-1 line-clamp-2">{row.message || row.commentaire}</p>}
                        {row.payment_url && row.payment_status !== "paid" && row.status !== "expirée" && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <button onClick={() => { navigator.clipboard.writeText(row.payment_url); toast.success("Lien de paiement copié"); }}
                              className="inline-flex items-center gap-1.5 text-xs text-brand font-medium hover:underline" data-testid={`copy-paylink-${row.id}`}>
                              <Phone size={13} /> Copier le lien de paiement
                            </button>
                            <button onClick={() => markDepositPaid(row.id)}
                              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-3 py-1 text-xs font-semibold hover:bg-emerald-700 transition-colors" data-testid={`mark-deposit-${row.id}`}>
                              ✓ Acompte encaissé sur place (CB)
                            </button>
                            {row.expires_at && (() => {
                              const hrs = Math.round((new Date(row.expires_at) - Date.now()) / 3600000);
                              return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${hrs <= 0 ? "bg-red-50 text-red-600" : hrs <= 12 ? "bg-amber-50 text-amber-700" : "bg-ink-100 text-ink-500"}`}>{hrs <= 0 ? "expiré" : `expire dans ${hrs} h`}</span>;
                            })()}
                          </div>
                        )}
                        {row.status === "expirée" && <span className="mt-1.5 inline-flex items-center rounded-full bg-red-50 text-red-600 px-2 py-0.5 text-[11px] font-medium">Réservation expirée · dates libérées</span>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {activeColl === "bookings" && (
                          <button onClick={() => setSelectedBooking(row)} className="rounded-lg bg-brand text-white px-4 py-2 text-xs font-semibold hover:bg-brand-600 transition-colors shadow-sm" data-testid={`manage-booking-${row.id}`}>
                            Gérer
                          </button>
                        )}
                        {activeColl === "bookings" ? (
                          <StatusBadge status={row.status} channel={row.smoobu_channel} isBlock={row.smoobu_is_block} />
                        ) : (
                          <select value={row.status || STATUS[activeColl][0]} onChange={(e) => setStatus(activeColl, row.id, e.target.value)}
                            className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs outline-none focus:border-brand" data-testid={`status-${row.id}`}>
                            {STATUS[activeColl].map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        )}
                        <button onClick={() => remove(activeColl, row.id)} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ACCOMMODATIONS */}
          {tab === "accommodations" && (
            <>
              <button onClick={() => setEdit({ coll: "accommodations", item: { type: "chambre", published: true, amenities: [], images: [] } })}
                className="mb-4 inline-flex items-center gap-2 rounded-full bg-brand text-white px-5 py-2.5 text-sm font-medium" data-testid="add-accommodation">
                <Plus size={16} /> Ajouter
              </button>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.map((a) => (
                  <div key={a.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <img src={mediaUrl(a.main_image)} alt={a.name} className="w-full aspect-[4/3] object-cover" />
                    <div className="p-4">
                      <p className="font-medium text-slate-900">{a.name}</p>
                      <p className="text-xs text-slate-500">{a.type} · {a.capacity} pers. {a.price_from ? `· ${a.price_from}€` : ""}</p>
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => setEdit({ coll: "accommodations", item: { ...a, amenities: (a.amenities || []).join(", ") } })} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-brand text-brand py-1.5 text-xs" data-testid={`edit-${a.slug}`}><Pencil size={14} /> Modifier</button>
                        <button onClick={() => remove("accommodations", a.id)} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* REVIEWS */}
          {activeColl === "reviews" && (
            <>
              <button onClick={() => setEdit({ coll: "reviews", item: { rating: 5, platform: "Google", published: true } })}
                className="mb-4 inline-flex items-center gap-2 rounded-full bg-brand text-white px-5 py-2.5 text-sm font-medium" data-testid="add-review">
                <Plus size={16} /> Ajouter un avis
              </button>
              <div className="space-y-3">
                {data.map((r) => (
                  <div key={r.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">{r.author} · {r.platform} · {"★".repeat(r.rating || 0)}</p>
                      <p className="text-xs text-slate-500 line-clamp-1">{r.text}</p>
                    </div>
                    <button onClick={() => setEdit({ coll: "reviews", item: r })} className="p-2 rounded-lg text-brand hover:bg-brand-50"><Pencil size={16} /></button>
                    <button onClick={() => remove("reviews", r.id)} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* SETTINGS */}
          {tab === "settings" && settings && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5 max-w-3xl">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nom du domaine" value={settings.brand || ""} onChange={(e) => setSettings({ ...settings, brand: e.target.value })} />
                <Field label="Téléphone" value={settings.phone || ""} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} data-testid="settings-phone" />
                <Field label="Téléphone mobile" value={settings.phone2 ?? "+33 7 64 31 80 35"} onChange={(e) => setSettings({ ...settings, phone2: e.target.value })} data-testid="settings-phone2" />
                <Field label="E-mail" value={settings.email || ""} onChange={(e) => setSettings({ ...settings, email: e.target.value })} data-testid="settings-email" />
                <Field label="Horaires d'accueil" value={settings.hours || ""} onChange={(e) => setSettings({ ...settings, hours: e.target.value })} />
                <Field label="Adresse" value={settings.address || ""} onChange={(e) => setSettings({ ...settings, address: e.target.value })} />
                <Field label="Taxe séjour · Chambre d'hôtes (€ / pers / nuit)" type="number" step="0.01" value={settings.tourist_tax_chambre ?? 1.5} onChange={(e) => setSettings({ ...settings, tourist_tax_chambre: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="settings-tax-chambre" />
                <Field label="Taxe séjour · Gîte non classé (% du coût nuitée / pers)" type="number" step="0.01" value={settings.tourist_tax_gite_pct ?? 3.15} onChange={(e) => setSettings({ ...settings, tourist_tax_gite_pct: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="settings-tax-gite-pct" />
                <Field label="Taxe séjour · Plafond (€ / pers / nuit)" type="number" step="0.01" value={settings.tourist_tax_cap ?? 4.40} onChange={(e) => setSettings({ ...settings, tourist_tax_cap: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="settings-tax-cap" />
                <Field label="WhatsApp" value={settings.whatsapp || ""} onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })} />
                <Field label="Facebook" value={settings.facebook || ""} onChange={(e) => setSettings({ ...settings, facebook: e.target.value })} />
                <Field label="Instagram" value={settings.instagram || ""} onChange={(e) => setSettings({ ...settings, instagram: e.target.value })} />
                <Field label="SIRET" value={settings.siret || ""} onChange={(e) => setSettings({ ...settings, siret: e.target.value })} />
                <Field label="Moteur de réservation (URL)" value={settings.booking_engine_url || ""} onChange={(e) => setSettings({ ...settings, booking_engine_url: e.target.value })} />
              </div>
              <p className="text-sm font-medium text-ink-700 pt-2">Chiffres clés</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Gîtes" value={settings.figures?.gites || ""} onChange={(e) => setSettings({ ...settings, figures: { ...settings.figures, gites: e.target.value } })} />
                <Field label="Chambres" value={settings.figures?.chambres || ""} onChange={(e) => setSettings({ ...settings, figures: { ...settings.figures, chambres: e.target.value } })} />
                <Field label="Capacité totale" value={settings.figures?.capacity_total || ""} onChange={(e) => setSettings({ ...settings, figures: { ...settings.figures, capacity_total: e.target.value } })} data-testid="settings-capacity" />
              </div>

              <p className="text-sm font-medium text-ink-700 pt-2">Photos — Espaces à vivre <span className="text-slate-400 font-normal">(page « Le Domaine »)</span></p>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  ["breakfast", "Petit déjeuner", "/api/files/begaudiere/uploads/86984761-ff97-4bfd-bcab-a891adc3f84b.png"],
                  ["bar", "Le bar", "/api/files/begaudiere/uploads/7ccadfb7-6673-406c-9390-5642f79b8466.webp"],
                  ["game", "Salle de jeux", "/api/files/begaudiere/uploads/8ae94926-002b-462d-b38f-64159689b16f.webp"],
                ].map(([k, label, fallback]) => (
                  <div key={k} className="rounded-lg border border-slate-200 p-3" data-testid={`space-${k}`}>
                    <p className="text-xs font-medium text-slate-600 mb-2">{label}</p>
                    <img src={(settings.spaces?.[k]) || fallback} alt={label} className="w-full aspect-[16/9] object-cover rounded-md mb-2" />
                    <label className="inline-flex items-center gap-1.5 text-sm text-brand cursor-pointer hover:underline">
                      <Pencil size={14} /> Remplacer la photo
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => setSpace(k, e.target.files?.[0])} data-testid={`space-upload-${k}`} />
                    </label>
                  </div>
                ))}
              </div>

              <p className="text-sm font-medium text-ink-700 pt-2">Galerie du domaine <span className="text-slate-400 font-normal">(les 9 premières sont affichées sur le site)</span></p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {(settings.gallery || []).map((img, i) => (
                  <div key={i} className={`relative group rounded-md ${i < 9 ? "ring-2 ring-brand ring-offset-1" : ""}`} data-testid={`gallery-item-${i}`}>
                    <img src={img} alt={`Galerie ${i + 1}`} className="w-full aspect-square object-cover rounded-md" />
                    {i < 9 && <span className="absolute top-1 left-1 rounded bg-brand text-white text-[10px] px-1.5 py-0.5">Affichée</span>}
                    <button onClick={() => removeGallery(i)} title="Supprimer" className="absolute top-1 right-1 w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition" data-testid={`gallery-remove-${i}`}><Trash2 size={14} /></button>
                    <div className="absolute bottom-1 inset-x-1 flex justify-between opacity-0 group-hover:opacity-100 transition">
                      <button onClick={() => moveGallery(i, -1)} disabled={i === 0} title="Avancer" className="w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center disabled:opacity-30" data-testid={`gallery-left-${i}`}>‹</button>
                      <button onClick={() => moveGallery(i, 1)} disabled={i === (settings.gallery.length - 1)} title="Reculer" className="w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center disabled:opacity-30" data-testid={`gallery-right-${i}`}>›</button>
                    </div>
                  </div>
                ))}
                <label className="flex flex-col items-center justify-center gap-1 aspect-square rounded-md border-2 border-dashed border-slate-300 text-slate-400 cursor-pointer hover:border-brand hover:text-brand transition" data-testid="gallery-add">
                  <Plus size={20} /><span className="text-xs">Ajouter</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addGallery(e.target.files)} />
                </label>
              </div>

              <p className="text-sm font-medium text-ink-700 pt-2">Menu « Les bocaux » <span className="text-slate-400 font-normal">(modifie la page de commande ET le bouton « Voir le menu »)</span></p>
              {(settings.bocaux_menu?.courses || []).map((c, ci) => (
                <div key={ci} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`menu-course-${ci}`}>
                  <div className="flex items-center gap-2">
                    <input value={c.label} onChange={(e) => setCourse(ci, "label", e.target.value)} className="flex-1 rounded border border-slate-200 px-2 py-1.5 text-sm font-medium" />
                    <div className="flex items-center gap-1"><input type="number" step="0.5" value={c.price} onChange={(e) => setCourse(ci, "price", Number(e.target.value))} className="w-20 rounded border border-slate-200 px-2 py-1.5 text-sm" /><span className="text-sm text-slate-400">€</span></div>
                  </div>
                  {(c.items || []).map((it, ii) => (
                    <div key={ii} className="flex items-center gap-2 pl-3">
                      {it.img ? <img src={it.img} alt="" className="w-12 h-12 rounded object-cover shrink-0" /> : null}
                      <div className="flex-1 space-y-1">
                        <input value={it.name} onChange={(e) => setItem(ci, ii, "name", e.target.value)} placeholder="Nom du plat" className="w-full rounded border border-slate-200 px-2 py-1 text-sm" />
                        <input value={it.desc} onChange={(e) => setItem(ci, ii, "desc", e.target.value)} placeholder="Description" className="w-full rounded border border-slate-200 px-2 py-1 text-xs text-slate-500" />
                      </div>
                      <label className="text-brand cursor-pointer hover:text-brand-600" title="Ajouter/Remplacer la photo">
                        <ImageIcon size={16} />
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const u = await uploadImage(f); setItem(ci, ii, "img", u); toast.success("Photo ajoutée — cliquez sur Enregistrer."); } catch (err) { toast.error(apiErr(err)); } }} data-testid={`menu-item-photo-${ci}-${ii}`} />
                      </label>
                      <button onClick={() => removeItem(ci, ii)} className="text-red-500 hover:text-red-700" title="Supprimer"><Trash2 size={15} /></button>
                    </div>
                  ))}
                  <button onClick={() => addItem(ci)} className="text-brand text-xs inline-flex items-center gap-1 pl-3"><Plus size={13} /> Ajouter un plat</button>
                </div>
              ))}
              <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="menu-drinks">
                <p className="text-sm font-medium">Boissons <span className="text-slate-400 font-normal text-xs">(33 cl)</span></p>
                {(settings.bocaux_menu?.drinks || []).map((d, di) => (
                  <div key={di} className="flex items-center gap-2 pl-3">
                    <input value={d.name} onChange={(e) => setDrink(di, "name", e.target.value)} placeholder="Boisson" className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm" />
                    <input type="number" step="0.5" value={d.price} onChange={(e) => setDrink(di, "price", Number(e.target.value))} className="w-20 rounded border border-slate-200 px-2 py-1 text-sm" /><span className="text-sm text-slate-400">€</span>
                    <button onClick={() => removeDrink(di)} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                  </div>
                ))}
                <button onClick={addDrink} className="text-brand text-xs inline-flex items-center gap-1 pl-3"><Plus size={13} /> Ajouter une boisson</button>
              </div>

              <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="menu-formulas">
                <p className="text-sm font-medium">Formules <span className="text-slate-400 font-normal text-xs">(combos affichés en haut de la page de commande)</span></p>
                {(settings.bocaux_menu?.formulas || []).map((f, fi) => (
                  <div key={fi} className="rounded border border-slate-200 p-2 space-y-2" data-testid={`formula-edit-${fi}`}>
                    <div className="flex items-center gap-2">
                      <input value={f.label} onChange={(e) => setFormula(fi, "label", e.target.value)} placeholder="Nom de la formule" className="flex-1 rounded border border-slate-200 px-2 py-1.5 text-sm font-medium" />
                      <div className="flex items-center gap-1"><input type="number" step="0.5" value={f.price} onChange={(e) => setFormula(fi, "price", Number(e.target.value))} className="w-20 rounded border border-slate-200 px-2 py-1.5 text-sm" /><span className="text-sm text-slate-400">€</span></div>
                      <button onClick={() => removeFormula(fi)} className="text-red-500 hover:text-red-700" title="Supprimer"><Trash2 size={15} /></button>
                    </div>
                    <input value={f.desc || ""} onChange={(e) => setFormula(fi, "desc", e.target.value)} placeholder="Description" className="w-full rounded border border-slate-200 px-2 py-1 text-xs text-slate-500" />
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      <span className="text-slate-500">Inclut :</span>
                      {(settings.bocaux_menu?.courses || []).map((c) => (
                        <label key={c.key} className="inline-flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={(f.courses || []).includes(c.key)} onChange={() => toggleFormulaCourse(fi, c.key)} className="accent-brand" /> {c.label}</label>
                      ))}
                      <label className="inline-flex items-center gap-1 ml-auto cursor-pointer"><input type="checkbox" checked={!!f.popular} onChange={(e) => setFormula(fi, "popular", e.target.checked)} className="accent-brand" /> « Le plus choisi »</label>
                    </div>
                  </div>
                ))}
                <button onClick={addFormula} className="text-brand text-xs inline-flex items-center gap-1"><Plus size={13} /> Ajouter une formule</button>
              </div>

              <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="galettes-editor">
                <p className="text-sm font-medium text-ink-700">Menu « Galettes &amp; Crêpes La Bégaudière » <span className="text-slate-400 font-normal text-xs">(2ᵉ menu de la page de commande · sur réservation)</span></p>
                {(settings.galettes_menu?.sections || []).map((sec, si) => (
                  <div key={si} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`gsection-${si}`}>
                    <div className="flex items-center gap-2">
                      <input value={sec.label} onChange={(e) => setGSection(si, "label", e.target.value)} className="flex-1 rounded border border-slate-200 px-2 py-1.5 text-sm font-medium" />
                      <button onClick={() => removeGSection(si)} className="text-red-500 hover:text-red-700" title="Supprimer la section"><Trash2 size={15} /></button>
                    </div>
                    {(sec.items || []).map((it, ii) => (
                      <div key={ii} className="flex items-center gap-2 pl-3">
                        <div className="flex-1 space-y-1">
                          <input value={it.name} onChange={(e) => setGItem(si, ii, "name", e.target.value)} placeholder="Nom" className="w-full rounded border border-slate-200 px-2 py-1 text-sm" />
                          <input value={it.desc || ""} onChange={(e) => setGItem(si, ii, "desc", e.target.value)} placeholder="Description (facultatif)" className="w-full rounded border border-slate-200 px-2 py-1 text-xs text-slate-500" />
                        </div>
                        <div className="flex items-center gap-1"><input type="number" step="0.5" value={it.price} onChange={(e) => setGItem(si, ii, "price", Number(e.target.value))} className="w-20 rounded border border-slate-200 px-2 py-1 text-sm" /><span className="text-sm text-slate-400">€</span></div>
                        <button onClick={() => removeGItem(si, ii)} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                      </div>
                    ))}
                    <button onClick={() => addGItem(si)} className="text-brand text-xs inline-flex items-center gap-1 pl-3"><Plus size={13} /> Ajouter un article</button>
                  </div>
                ))}
                <button onClick={addGSection} className="text-brand text-xs inline-flex items-center gap-1"><Plus size={13} /> Ajouter une section</button>
              </div>

              <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="livret-editor">
                <p className="text-sm font-medium">Livret d'accueil <span className="text-slate-400 font-normal text-xs">(page privée /livret + QR)</span></p>
                <textarea value={L.welcome || ""} onChange={(e) => setLivret("welcome", e.target.value)} placeholder="Message de bienvenue" rows={2} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" />
                <div className="grid sm:grid-cols-2 gap-2">
                  <label className="text-xs text-slate-500">Petit-déj (semaine)<input value={L.breakfast_week || ""} onChange={(e) => setLivret("breakfast_week", e.target.value)} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" /></label>
                  <label className="text-xs text-slate-500">Petit-déj (week-end)<input value={L.breakfast_weekend || ""} onChange={(e) => setLivret("breakfast_weekend", e.target.value)} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" /></label>
                  <label className="text-xs text-slate-500">Wi-Fi (SSID)<input value={L.wifi_ssid || ""} onChange={(e) => setLivret("wifi_ssid", e.target.value)} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" /></label>
                  <label className="text-xs text-slate-500">Wi-Fi (mot de passe)<input value={L.wifi_password || ""} onChange={(e) => setLivret("wifi_password", e.target.value)} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" /></label>
                  <label className="text-xs text-slate-500">Code porte<input value={L.door_code || ""} onChange={(e) => setLivret("door_code", e.target.value)} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" /></label>
                  <label className="text-xs text-slate-500">Heure de départ<input value={L.checkout || ""} onChange={(e) => setLivret("checkout", e.target.value)} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" /></label>
                  <label className="text-xs text-slate-500">Petit-déj (note, ex : buffet)<input value={L.breakfast_note || ""} onChange={(e) => setLivret("breakfast_note", e.target.value)} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" /></label>
                  <label className="text-xs text-slate-500">Salle de jeux (accès)<input value={L.games_hours || ""} onChange={(e) => setLivret("games_hours", e.target.value)} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" /></label>
                </div>
                <textarea value={L.breakfast_details || ""} onChange={(e) => setLivret("breakfast_details", e.target.value)} placeholder="Petit-déjeuner : explications (lieu, déroulement…) — affiché aussi sur le lien d'arrivée tardive" rows={3} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" data-testid="livret-breakfast-details" />
                <textarea value={L.games || ""} onChange={(e) => setLivret("games", e.target.value)} placeholder="Salle de jeux (baby-foot, billard, jeux vidéo…)" rows={2} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" />
                <textarea value={L.food_warning || ""} onChange={(e) => setLivret("food_warning", e.target.value)} placeholder="Avertissement : nourriture extérieure interdite" rows={2} className="w-full rounded border border-red-200 bg-red-50/40 px-2 py-1.5 text-sm" />
                <input value={L.contact_phone || ""} onChange={(e) => setLivret("contact_phone", e.target.value)} placeholder="Téléphone de contact (facultatif)" className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" />
                <div>
                  <p className="text-xs text-slate-500 mb-1">À faire dans les environs</p>
                  {(L.places || []).map((p, pi) => (
                    <div key={pi} className="flex items-center gap-2 mb-1">
                      <input value={p.name || ""} onChange={(e) => setLivretPlace(pi, "name", e.target.value)} placeholder="Lieu" className="w-1/3 rounded border border-slate-200 px-2 py-1 text-sm" />
                      <input value={p.distance || ""} onChange={(e) => setLivretPlace(pi, "distance", e.target.value)} placeholder="Distance" className="w-24 rounded border border-slate-200 px-2 py-1 text-sm" />
                      <input value={p.desc || ""} onChange={(e) => setLivretPlace(pi, "desc", e.target.value)} placeholder="Description" className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm" />
                      <button onClick={() => removeLivretPlace(pi)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <button onClick={addLivretPlace} className="text-brand text-xs inline-flex items-center gap-1"><Plus size={13} /> Ajouter un lieu</button>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Rappels importants</p>
                  {(L.reminders || []).map((r, ri) => (
                    <div key={ri} className="flex items-center gap-2 mb-1">
                      <input value={r} onChange={(e) => setLivretReminder(ri, e.target.value)} className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm" />
                      <button onClick={() => removeLivretReminder(ri)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <button onClick={addLivretReminder} className="text-brand text-xs inline-flex items-center gap-1"><Plus size={13} /> Ajouter un rappel</button>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Suppléments & tarifs <span className="text-slate-400">(lit bébé, personnes non déclarées…)</span></p>
                  {(L.supplements || []).map((sp, si) => (
                    <div key={si} className="flex items-center gap-2 mb-1">
                      <input value={sp} onChange={(e) => setLivretSupplement(si, e.target.value)} className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm" />
                      <button onClick={() => removeLivretSupplement(si)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <button onClick={addLivretSupplement} className="text-brand text-xs inline-flex items-center gap-1"><Plus size={13} /> Ajouter un supplément</button>
                </div>
                <textarea value={L.dining || ""} onChange={(e) => setLivret("dining", e.target.value)} placeholder="Restauration sur place" rows={2} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" />
              </div>

              <button onClick={saveSettings} className="rounded-full bg-brand text-white px-7 py-3 font-medium hover:bg-brand-600" data-testid="settings-save">Enregistrer</button>
            </div>
          )}
          </div>
        </main>
      </div>

      {/* PHONE BOOKING DIALOG */}
      <PhoneBookingDialog open={phoneOpen} onOpenChange={setPhoneOpen} onCreated={reloadActive} />

      {/* BOOKING DETAILS / MANAGEMENT DIALOG */}
      <BookingDetailsDialog
        booking={selectedBooking}
        onClose={() => setSelectedBooking(null)}
        onRefunded={() => { setSelectedBooking(null); reloadActive(); }}
        onUpdated={reloadActive}
      />

      {/* EDIT DIALOG */}
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="bg-cream max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{edit?.item?.id ? "Modifier" : "Ajouter"}</DialogTitle></DialogHeader>
          {edit && <EditForm edit={edit} onSave={saveItem} onCancel={() => setEdit(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditForm({ edit, onSave, onCancel }) {
  const [item, setItem] = useState(edit.item);
  const s = (k) => (e) => setItem({ ...item, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  if (edit.coll === "reviews") {
    return (
      <div className="space-y-4">
        <Field label="Auteur" value={item.author || ""} onChange={s("author")} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Plateforme" value={item.platform || ""} onChange={s("platform")} />
          <Field label="Note (1-5)" type="number" min="1" max="5" value={item.rating || 5} onChange={(e) => setItem({ ...item, rating: Number(e.target.value) })} />
        </div>
        <label className="block"><span className="text-xs font-medium text-ink-600 mb-1 block">Avis</span>
          <textarea rows={3} value={item.text || ""} onChange={s("text")} className="w-full rounded-lg border border-ink-400/25 px-3 py-2 text-sm outline-none focus:border-brand" /></label>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Type de séjour" value={item.stay_type || ""} onChange={s("stay_type")} />
          <Field label="Date" value={item.date || ""} onChange={s("date")} />
        </div>
        <Actions onSave={() => onSave("reviews", item)} onCancel={onCancel} />
      </div>
    );
  }

  // accommodations
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nom" value={item.name || ""} onChange={s("name")} />
        <label className="block"><span className="text-xs font-medium text-ink-600 mb-1 block">Type</span>
          <select value={item.type || "chambre"} onChange={s("type")} className="w-full rounded-lg border border-ink-400/25 px-3 py-2 text-sm">
            <option value="chambre">Chambre d'hôtes</option><option value="gite">Gîte</option>
          </select></label>
        <Field label="Slug (URL)" value={item.slug || ""} onChange={s("slug")} />
        <Field label="Capacité" type="number" value={item.capacity || ""} onChange={(e) => setItem({ ...item, capacity: Number(e.target.value) })} />
        <Field label="Superficie (m²)" type="number" value={item.surface || ""} onChange={(e) => setItem({ ...item, surface: e.target.value ? Number(e.target.value) : null })} />
        <Field label="Couchages" value={item.beds || ""} onChange={s("beds")} />
        <Field label="Tarif à partir de (€)" type="number" value={item.price_from || ""} onChange={(e) => setItem({ ...item, price_from: e.target.value ? Number(e.target.value) : null })} />
        <Field label="Arrivée" value={item.checkin || ""} onChange={s("checkin")} />
        <Field label="Départ" value={item.checkout || ""} onChange={s("checkout")} />
        <Field label="Animaux" value={item.pets || ""} onChange={s("pets")} />
      </div>
      <PhotoManager images={item.images || []} mainImage={item.main_image || ""} onChange={({ images, main_image }) => setItem({ ...item, images, main_image })} />
      <ArrivalStepsEditor steps={item.arrival_steps} onChange={(arrival_steps) => setItem({ ...item, arrival_steps })} />
      <Field label="Équipements (séparés par des virgules)" value={typeof item.amenities === "string" ? item.amenities : (item.amenities || []).join(", ")} onChange={s("amenities")} />
      <label className="block"><span className="text-xs font-medium text-ink-600 mb-1 block">Description</span>
        <textarea rows={3} value={item.description || ""} onChange={s("description")} className="w-full rounded-lg border border-ink-400/25 px-3 py-2 text-sm outline-none focus:border-brand" /></label>
      <label className="flex items-center gap-2 text-sm text-ink-600"><input type="checkbox" checked={!!item.published} onChange={s("published")} className="accent-brand w-4 h-4" /> Publié</label>
      <Actions onSave={() => onSave("accommodations", item)} onCancel={onCancel} />
    </div>
  );
}

function Actions({ onSave, onCancel }) {
  return (
    <div className="flex gap-3 pt-2">
      <button onClick={onSave} className="flex-1 rounded-full bg-brand text-white py-2.5 font-medium hover:bg-brand-600" data-testid="edit-save">Enregistrer</button>
      <button onClick={onCancel} className="rounded-full border border-ink-400/30 text-ink-600 px-6 py-2.5"><X size={16} /></button>
    </div>
  );
}
